import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Camera, ExternalLink, GitCompare, Link2, Loader2, X } from 'lucide-react';
import { captureIframeCanvasToFile } from '../../utils/boardSnapshotCapture';
import { defaultPreviousHtmlModel, type HtmlCompareModel } from '../../utils/htmlCompareFilter';
import { useHtmlViewerCameraSync } from '../../utils/htmlViewerCameraSync';
import { useCompareSyncHotkey } from '../../hooks/useCompareSyncHotkey';
import HtmlComparePicker from './HtmlComparePicker';
import HtmlViewerPane from './HtmlViewerPane';
import './HtmlViewerModal.css';
import SharedWholeProjectNote from '../pm/SharedWholeProjectNote';

export type HtmlViewerSibling = HtmlCompareModel;

export interface HtmlViewerModalProps {
  /** Fallback navigation URL (hosted proxy or Storage). */
  url: string;
  /** Direct Storage download URL — used to fetch HTML for same-origin blob preview. */
  fetchUrl?: string;
  title: string;
  versionLabel?: string;
  onClose: () => void;
  /** Orbit the model, then capture the current view as a board image. */
  captureMode?: boolean;
  onCapture?: (file: File) => void | Promise<void>;
  projectId?: string;
  projectName?: string;
  streamId?: string;
  documentId?: string;
  siblingModels?: HtmlViewerSibling[];
  /** This document is the one whole-project file, opened from any structure track. */
  sharedAcrossStructures?: boolean;
}

export default function HtmlViewerModal({
  url,
  fetchUrl,
  title,
  versionLabel,
  onClose,
  captureMode,
  onCapture,
  projectId,
  projectName,
  documentId,
  siblingModels = [],
  sharedAcrossStructures = false,
}: HtmlViewerModalProps) {
  const screenshotInputRef = useRef<HTMLInputElement>(null);
  const splitStageRef = useRef<HTMLDivElement>(null);
  const [splitDragging, setSplitDragging] = useState(false);
  const [leftIframe, setLeftIframe] = useState<HTMLIFrameElement | null>(null);
  const [rightIframe, setRightIframe] = useState<HTMLIFrameElement | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [capturedCount, setCapturedCount] = useState(0);
  const [compareOn, setCompareOn] = useState(false);
  const [syncOn, setSyncOn] = useState(true);
  const [rightModel, setRightModel] = useState<HtmlCompareModel | null>(null);
  const [splitRatio, setSplitRatio] = useState(0.5);
  const [compareAllowed, setCompareAllowed] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= 900,
  );

  const currentDocumentId =
    documentId
    || siblingModels.find(m => m.fetchUrl === fetchUrl || m.url === url)?.documentId
    || '';

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const frames = [leftIframe, rightIframe].filter((frame): frame is HTMLIFrameElement => !!frame);
    const detach: Array<() => void> = [];
    for (const frame of frames) {
      const attach = () => {
        try {
          const win = frame.contentWindow;
          if (!win) return;
          win.removeEventListener('keydown', onKeyDown, true);
          win.addEventListener('keydown', onKeyDown, true);
        } catch {
          // A cross-origin frame keeps its own keys.
        }
      };
      attach();
      frame.addEventListener('load', attach);
      detach.push(() => {
        frame.removeEventListener('load', attach);
        try {
          frame.contentWindow?.removeEventListener('keydown', onKeyDown, true);
        } catch {
          /* ignore */
        }
      });
    }
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      detach.forEach((fn) => fn());
    };
  }, [onClose, leftIframe, rightIframe]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 900px)');
    const apply = () => setCompareAllowed(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (!compareAllowed && compareOn) {
      setCompareOn(false);
      setRightModel(null);
    }
  }, [compareAllowed, compareOn]);

  const splitOpen = compareOn && compareAllowed && !captureMode;
  useHtmlViewerCameraSync(leftIframe, rightIframe, splitOpen && syncOn && !!rightModel);
  useCompareSyncHotkey(splitOpen, () => setSyncOn(v => !v), leftIframe, rightIframe);

  const openHref = fetchUrl || url;
  const safeName = (title || 'model').replace(/[^\w.-]+/g, '_').slice(0, 60);

  const finishCapture = async (file: File) => {
    setCaptureError(null);
    await onCapture?.(file);
    setCapturedCount(n => n + 1);
  };

  const handleCapture = async () => {
    if (!onCapture || capturing) return;
    setCapturing(true);
    setCaptureError(null);
    try {
      const iframe = leftIframe;
      const file = iframe
        ? await captureIframeCanvasToFile(iframe, `${safeName}-snapshot.jpg`)
        : null;
      if (!file) {
        setCaptureError('Couldn’t grab this view automatically. Upload a screenshot instead.');
        return;
      }
      await finishCapture(file);
    } finally {
      setCapturing(false);
    }
  };

  const toggleCompare = useCallback(() => {
    if (compareOn) {
      setCompareOn(false);
      setRightModel(null);
      return;
    }
    setSyncOn(true);
    setRightModel(defaultPreviousHtmlModel(siblingModels, currentDocumentId));
    setCompareOn(true);
  }, [compareOn, siblingModels, currentDocumentId]);

  const setRatioFromClientX = useCallback((clientX: number) => {
    const stage = splitStageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    if (rect.width < 1) return;
    setSplitRatio(Math.max(0.28, Math.min(0.72, (clientX - rect.left) / rect.width)));
  }, []);

  const onDividerPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setSplitDragging(true);
    setRatioFromClientX(e.clientX);
  };

  useEffect(() => {
    if (!splitDragging) return;
    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      setRatioFromClientX(ev.clientX);
    };
    const onUp = () => setSplitDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [splitDragging, setRatioFromClientX]);

  return createPortal(
    <div
      className="html-viewer-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`html-viewer-dialog${splitOpen ? ' is-split' : ''}${splitDragging ? ' is-resizing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="3D model preview"
      >
        <div className="html-viewer-header">
          <div className="html-viewer-title">
            <Box size={16} aria-hidden />
            <span className="html-viewer-title-name">{title}</span>
            {versionLabel ? (
              <span className="html-viewer-version">{versionLabel}</span>
            ) : null}
          </div>
          <div className="html-viewer-actions">
            {captureMode && onCapture ? (
              <>
                {capturedCount > 0 ? (
                  <span className="html-viewer-capture-count">
                    {capturedCount} added
                  </span>
                ) : null}
                <button
                  type="button"
                  className="html-viewer-capture-btn"
                  onClick={() => void handleCapture()}
                  disabled={capturing || !leftIframe}
                  title="Add the current view to the inspiration board, then orbit and capture more"
                >
                  {capturing ? <Loader2 size={14} className="html-viewer-spinner" /> : <Camera size={14} />}
                  {capturedCount > 0 ? 'Capture another view' : 'Capture this view'}
                </button>
                {capturedCount > 0 ? (
                  <button
                    type="button"
                    className="html-viewer-done-btn"
                    onClick={onClose}
                  >
                    Done
                  </button>
                ) : null}
              </>
            ) : null}
            {!captureMode && compareAllowed ? (
              <button
                type="button"
                className={`html-viewer-toolbar-btn html-viewer-compare-btn${splitOpen ? ' is-active' : ''}`}
                onClick={toggleCompare}
                aria-pressed={splitOpen}
                title={
                  splitOpen
                    ? 'Close compare and return to a single pane'
                    : 'Open another 3D model beside this one'
                }
              >
                <GitCompare size={14} />
                Compare
              </button>
            ) : null}
            {splitOpen ? (
              <button
                type="button"
                className={`html-viewer-toolbar-btn${syncOn ? ' is-active' : ''}`}
                onClick={() => setSyncOn(v => !v)}
                aria-pressed={syncOn}
                title={
                  syncOn
                    ? 'Sync is on — both models share camera orbit and zoom. S or click to look at each model independently'
                    : 'Keep both models on the same camera pose (S). Either pane can lead'
                }
              >
                <Link2 size={14} />
                Sync
              </button>
            ) : null}
            <a
              href={openHref}
              target="_blank"
              rel="noopener noreferrer"
              className="html-viewer-icon-btn"
              title="Open in new tab"
            >
              <ExternalLink size={14} />
            </a>
            <button
              type="button"
              className="html-viewer-icon-btn"
              onClick={onClose}
              title="Close"
              aria-label="Close 3D model preview"
            >
              <X size={14} />
            </button>
          </div>
        </div>
        {sharedAcrossStructures ? <SharedWholeProjectNote /> : null}
        {captureMode && onCapture && capturedCount === 0 && !captureError ? (
          <div className="html-viewer-capture-hint">
            Orbit to the view you want, capture it, then capture more angles. Close when you are done.
          </div>
        ) : null}
        {captureMode && captureError ? (
          <div className="html-viewer-capture-banner">
            <span>{captureError}</span>
            <button
              type="button"
              className="html-viewer-capture-upload"
              onClick={() => screenshotInputRef.current?.click()}
            >
              Upload a screenshot
            </button>
            <input
              ref={screenshotInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file || capturing) return;
                setCapturing(true);
                void finishCapture(file).finally(() => setCapturing(false));
              }}
            />
          </div>
        ) : null}
        <div
          ref={splitStageRef}
          className={`html-viewer-split-stage${splitOpen ? ' is-split' : ''}`}
        >
          <div
            className="html-viewer-split-left"
            style={splitOpen ? { flex: `${splitRatio} 1 0` } : undefined}
          >
            <HtmlViewerPane
              url={url}
              fetchUrl={fetchUrl}
              title={title}
              iframeRef={setLeftIframe}
              chrome={
                splitOpen ? (
                  <div className="html-viewer-pane-heading">
                    <div className="html-viewer-pane-project" title={projectName || title}>
                      {projectName || 'This project'}
                    </div>
                    <div className="html-viewer-pane-meta">{versionLabel || title}</div>
                  </div>
                ) : undefined
              }
            />
          </div>
          {splitOpen ? (
            <>
              <div
                className={`html-viewer-split-divider${splitDragging ? ' is-dragging' : ''}`}
                role="separator"
                aria-orientation="vertical"
                aria-valuemin={28}
                aria-valuemax={72}
                aria-valuenow={Math.round(splitRatio * 100)}
                aria-label="Resize compare panes"
                onPointerDown={onDividerPointerDown}
              />
              <div className="html-viewer-split-right" style={{ flex: `${1 - splitRatio} 1 0` }}>
                <HtmlViewerPane
                  url={rightModel?.url || ''}
                  fetchUrl={rightModel?.fetchUrl}
                  title={rightModel?.title || 'Compare'}
                  iframeRef={setRightIframe}
                  empty={!rightModel}
                  chrome={
                    <div className="html-viewer-pane-heading">
                      <div
                        className="html-viewer-pane-project"
                        title={rightModel?.projectName || projectName || 'Compare'}
                      >
                        {rightModel?.projectName || projectName || 'Choose a project'}
                      </div>
                      <HtmlComparePicker
                        currentDocumentId={currentDocumentId}
                        currentProjectId={projectId}
                        thisProjectModels={siblingModels}
                        selected={rightModel}
                        onSelect={setRightModel}
                      />
                    </div>
                  }
                />
              </div>
              {splitDragging ? (
                <div
                  className="html-viewer-split-drag-overlay"
                  onPointerMove={e => setRatioFromClientX(e.clientX)}
                  onPointerUp={() => setSplitDragging(false)}
                  onPointerCancel={() => setSplitDragging(false)}
                />
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
